<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { FieldValidationResult, ERROR_ANCHOR_CLASS, FORM_BLUR_EVENT } from './FormValidation';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const props = withDefaults(defineProps<{
    labelKey?: string;
    labelFor?: string;
    validationState?: FieldValidationResult;
    appendLabel?: boolean;
    inline?: boolean;
    required?: boolean;
}>(), {
    appendLabel: false,
    inline: false
});

const inputBlurredWithError = ref(false);

const showValidationMessage = computed(() => 
    !!props.validationState?.invalid && (inputBlurredWithError.value || props.validationState?.submitAttempted));

const fieldClass = computed(() => 
    ({[ERROR_ANCHOR_CLASS]: showValidationMessage.value, field: true, 'mb-0': props.inline, 'flex': props.appendLabel})
);

function onBlur() {
    dispatchEvent(new Event(FORM_BLUR_EVENT))

    requestAnimationFrame(() => {
        if (props.validationState?.invalid === true) 
            inputBlurredWithError.value = true;
    });
}

onUnmounted(() => {
    removeEventListener(FORM_BLUR_EVENT, onBlur);
})

</script>

<template>
    <div :class="fieldClass">
        <label v-if="labelKey && !appendLabel" class="flex gap-1">
            {{ t(labelKey) }} <span v-if="props.required" class="required-input">*</span>
            <span class="ml-auto"><slot name="action" /></span>
        </label>
        <div v-tooltip.bottom="{ value: validationState?.message, disabled: !showValidationMessage || !inline }">
            <slot :on-blur="onBlur" />
            <label 
                v-if="labelKey && appendLabel"
                class="ml-2 cursor-pointer"
                :for="props.labelFor"
            >
                {{ t(labelKey) }} 
                <span v-if="props.required" class="required-input">*</span>
            </label>
        </div>
        <span v-if="showValidationMessage && !inline" class="error-text"> {{ validationState?.message }} </span>
    </div>
</template>

<style scoped lang="scss">
    $error-color: rgb(250, 85, 78);
    .form-validation-input-error {
        input, textarea {
            border: 2px solid $error-color;
        }
        .error-text {
            padding-left: 12px;
            font-size: 12px;
            color: $error-color;
            display: block;
        }
    }
    :deep(.form-validation-input-error) {
        input, fieldset, .p-dropdown, .p-multiselect {
            border: 2px solid $error-color;
        }
    }
</style>
